import React, { useEffect, useState, Component } from 'react';
import { InfoIcon, Loader2Icon, AlertCircleIcon, CheckCircleIcon } from 'lucide-react';
import { useAuth } from '../../components/auth/AuthProvider';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
export const ProductPreferencesSection = () => {
  const {
    user
  } = useAuth();
  // State management
  const [preferencesData, setPreferencesData] = useState({
    defaultVisibility: 'public',
    defaultCategory: 'UI Kits',
    defaultExternalLink: 'https://yourportfolio.com/project',
    defaultPrice: '29.99'
  });
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    ...preferencesData
  });
  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Category options
  const categoryOptions = ['Website Templates', 'Mobile App Kits', 'UI Kits', 'AI Prompt Packs', 'Component Libraries', 'Design Systems', 'Animations', 'Audio Packs', 'No-Code MVPs', 'Notion Templates', 'eBooks & PDF Guides', 'Course Slides & Templates', 'Printable Planners', 'Email Templates & Flows', 'Copywriting Packs', 'Fonts & Typefaces', 'Icon Packs', '3D Assets & Mockups', 'Canva Templates', 'Social Media Content Packs', 'Legal Templates', 'Resume & Portfolio Templates', 'Other'];
  // Fetch product preferences from Firestore
  useEffect(() => {
    const fetchPreferences = async () => {
      if (!user?.id) return;
      setIsLoading(true);
      setError(null);
      try {
        const preferencesDocRef = doc(db, 'userPreferences', user.id);
        const preferencesDoc = await getDoc(preferencesDocRef);
        if (preferencesDoc.exists()) {
          const data = preferencesDoc.data();
          // Initialize preferences data from Firestore
          const productPrefs = {
            defaultVisibility: data.productPreferences?.defaultVisibility || 'public',
            defaultCategory: data.productPreferences?.defaultCategory || 'UI Kits',
            defaultExternalLink: data.productPreferences?.defaultExternalLink || 'https://yourportfolio.com/project',
            defaultPrice: data.productPreferences?.defaultPrice || '29.99'
          };
          setPreferencesData(productPrefs);
          setFormData(productPrefs);
        } else {
          // Create a new preferences document if it doesn't exist
          const defaultPreferences = {
            productPreferences: {
              defaultVisibility: 'public',
              defaultCategory: 'UI Kits',
              defaultExternalLink: 'https://yourportfolio.com/project',
              defaultPrice: '29.99'
            },
            createdAt: new Date(),
            userId: user.id
          };
          await setDoc(preferencesDocRef, defaultPreferences);
          setPreferencesData(defaultPreferences.productPreferences);
          setFormData(defaultPreferences.productPreferences);
        }
      } catch (err) {
        console.error('Error fetching product preferences:', err);
        setError('Failed to load preferences. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchPreferences();
  }, [user]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const validateForm = () => {
    const errors: Record<string, string> = {};
    // Validate price
    if (formData.defaultPrice) {
      const priceValue = parseFloat(formData.defaultPrice);
      if (isNaN(priceValue) || priceValue < 0) {
        errors.defaultPrice = 'Please enter a valid price';
      }
    }
    return errors;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    // Validate form
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setError(Object.values(validationErrors)[0]);
      return;
    }
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const preferencesDocRef = doc(db, 'userPreferences', user.id);
      // Get current data to update only the product preferences
      const preferencesDoc = await getDoc(preferencesDocRef);
      if (preferencesDoc.exists()) {
        // Update only the productPreferences field
        await updateDoc(preferencesDocRef, {
          productPreferences: formData,
          updatedAt: new Date()
        });
      } else {
        // Create a new document if it doesn't exist
        await setDoc(preferencesDocRef, {
          productPreferences: formData,
          createdAt: new Date(),
          userId: user.id
        });
      }
      // Update local state
      setPreferencesData({
        ...formData
      });
      setSuccessMessage('Product preferences updated successfully!');
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating product preferences:', err);
      setError('Failed to update preferences. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  const handleCancelEdit = () => {
    setFormData({
      ...preferencesData
    });
    setError(null);
    setSuccessMessage(null);
    setIsEditing(false);
  };
  if (isLoading) {
    return <div className="flex justify-center items-center min-h-[300px]" data-id="element-3359">
        <Loader2Icon className="h-8 w-8 text-[#2DB489] dark:text-[#4ED1A0] animate-spin" data-id="element-3360" />
        <span className="ml-2 text-gray-600 dark:text-[#B0C1BD]" data-id="element-3361">
          Loading preferences...
        </span>
      </div>;
  }
  return <div data-id="element-3362">
      <div className="flex justify-between items-center mb-6" data-id="element-3363">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-3364">
          Product Preferences
        </h2>
        <button onClick={() => {
        setIsEditing(!isEditing);
        if (isEditing) {
          handleCancelEdit();
        }
      }} className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-[#1A3330] text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-colors" disabled={isSaving} data-id="element-3365">
          {isEditing ? 'Cancel' : 'Edit Preferences'}
        </button>
      </div>
      {/* Success message */}
      {successMessage && <div className="mb-6 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30 rounded-md text-green-800 dark:text-green-400 flex items-start" data-id="element-3366">
          <CheckCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-3367" />
          <span data-id="element-3368">{successMessage}</span>
        </div>}
      {/* Error message */}
      {error && <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-md text-red-800 dark:text-red-400 flex items-start" data-id="element-3369">
          <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-3370" />
          <span data-id="element-3371">{error}</span>
        </div>}
      <form onSubmit={handleSubmit} data-id="element-3372">
        <div className="space-y-8 mb-8" data-id="element-3373">
          {/* Default Product Visibility */}
          <div data-id="element-3374">
            <label htmlFor="defaultVisibility" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3375">
              Default Product Visibility
            </label>
            {isEditing ? <div className="max-w-xs" data-id="element-3376">
                <select id="defaultVisibility" name="defaultVisibility" value={formData.defaultVisibility} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3377">
                  <option value="public" data-id="element-3378">Public</option>
                  <option value="private" data-id="element-3379">Private</option>
                  <option value="unlisted" data-id="element-3380">Unlisted</option>
                </select>
              </div> : <p className="text-gray-900 dark:text-[#F8FAF9] capitalize" data-id="element-3381">
                {preferencesData.defaultVisibility}
              </p>}
            <p className="mt-1 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3382">
              New products will default to this visibility. You can change it
              per product.
            </p>
          </div>
          {/* Default Product Category */}
          <div data-id="element-3383">
            <label htmlFor="defaultCategory" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3384">
              Default Product Category
            </label>
            {isEditing ? <div className="max-w-xs" data-id="element-3385">
                <select id="defaultCategory" name="defaultCategory" value={formData.defaultCategory} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3386">
                  {categoryOptions.map(category => <option key={category} value={category} data-id="element-3387">
                      {category}
                    </option>)}
                </select>
              </div> : <p className="text-gray-900 dark:text-[#F8FAF9]" data-id="element-3388">
                {preferencesData.defaultCategory}
              </p>}
            <p className="mt-1 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3389">
              This category will be pre-selected when adding new products.
            </p>
          </div>
          {/* Default Price Template */}
          <div data-id="element-3390">
            <label htmlFor="defaultPrice" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3391">
              Default Price Template (USD)
            </label>
            {isEditing ? <div className="max-w-xs relative" data-id="element-3392">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 dark:text-[#7A8F8A]" data-id="element-3393">
                  $
                </span>
                <input type="text" id="defaultPrice" name="defaultPrice" value={formData.defaultPrice} onChange={handleInputChange} placeholder="29.99" className="w-full pl-8 px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3394" />
              </div> : <p className="text-gray-900 dark:text-[#F8FAF9]" data-id="element-3395">
                ${preferencesData.defaultPrice}
              </p>}
            <p className="mt-1 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3396">
              This price will be pre-filled when adding new products.
            </p>
          </div>
          {/* Default External Link */}
          <div data-id="element-3397">
            <label htmlFor="defaultExternalLink" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3398">
              Default External Link
            </label>
            {isEditing ? <div className="max-w-lg" data-id="element-3399">
                <input type="text" id="defaultExternalLink" name="defaultExternalLink" value={formData.defaultExternalLink} onChange={handleInputChange} placeholder="https://example.com/project" className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3400" />
              </div> : <p className="text-gray-900 dark:text-[#F8FAF9]" data-id="element-3401">
                {preferencesData.defaultExternalLink}
              </p>}
            <p className="mt-1 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3402">
              This link will be auto-filled in the "Add Product" form.
            </p>
          </div>
          {/* Upload Limit Info */}
          <div className="bg-gray-50 dark:bg-[#162825] rounded-lg p-4 border border-gray-200 dark:border-[#1A3330]" data-id="element-3403">
            <div className="flex items-start" data-id="element-3404">
              <InfoIcon className="w-5 h-5 text-gray-400 dark:text-[#7A8F8A] mr-3 mt-0.5 flex-shrink-0" data-id="element-3405" />
              <div data-id="element-3406">
                <h4 className="text-sm font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-3407">
                  Upload Limits
                </h4>
                <p className="text-sm text-gray-600 dark:text-[#B0C1BD]" data-id="element-3408">
                  Maximum upload size per file: 1 GB
                </p>
                <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mt-1" data-id="element-3409">
                  Supported file formats: ZIP, PDF, PSD, AI, FIG, XD, SKETCH,
                  MP4, MP3
                </p>
              </div>
            </div>
          </div>
        </div>
        {isEditing && <div className="flex justify-end" data-id="element-3410">
            <button type="button" onClick={handleCancelEdit} className="px-5 py-2.5 mr-3 border border-gray-300 dark:border-[#1A3330] text-gray-700 dark:text-[#B0C1BD] font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-all duration-200" disabled={isSaving} data-id="element-3411">
              Cancel
            </button>
            <button type="submit" className="px-5 py-2.5 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white font-medium rounded-lg hover:shadow-md transition-all duration-200 flex items-center" disabled={isSaving} data-id="element-3412">
              {isSaving ? <>
                  <Loader2Icon className="h-4 w-4 mr-2 animate-spin" data-id="element-3413" />
                  Saving...
                </> : 'Save Changes'}
            </button>
          </div>}
      </form>
    </div>;
};